import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Spinner } from 'react-bootstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { useGetShareOfVoiceTrendQuery } from '../../../api/trackerEndpointsApi';
import { useProject } from '../../../context/ProjectContext';
import { useProjectCompetitors } from '../../../hooks/useProjectCompetitors';
import { brandInDomainFilter, isDomainFocusBrand } from '../../../utils/domain-filter-utils';
import { PANEL_FONT_FAMILY } from '../../../utils/panel-font';
import { buildDateRangeQueryOptions } from '../../../utils/visibility-range';
import { brandSeriesColor, chartChrome, escapeHtml } from '../../../utils/competition-chart';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import { DashboardEmptyNote } from '../DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './ChartLegend';

export function ShareOfVoiceTrendCard() {
  const {
    projectId,
    visibilityRange,
    customFrom,
    customTo,
    topicFilterId,
    modelFilterId,
    domainFilterId,
  } = useProject();
  const competitors = useProjectCompetitors(projectId);
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };

  const { data: trend, isFetching, isError, error, refetch } = useGetShareOfVoiceTrendQuery(
    projectId
      ? {
          projectId,
          ...buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
            topicId: topicFilterId ?? undefined,
            providerIds: modelFilterId ? [modelFilterId] : undefined,
          }),
        }
      : skipToken,
  );

  const series = useMemo(() => {
    let competitorIndex = 0;
    return (trend?.brands ?? [])
      .filter((brand) =>
        brandInDomainFilter(
          { brandId: brand.brandId, name: brand.name, isOwn: brand.relationship === 'own_brand' },
          domainFilterId,
          competitors,
        ),
      )
      .map((brand) => {
        const isFocus = isDomainFocusBrand(
          { brandId: brand.brandId, name: brand.name, isOwn: brand.relationship === 'own_brand' },
          domainFilterId,
          competitors,
        );
        return { ...brand, isOwn: isFocus, color: brandSeriesColor(isFocus, isFocus ? 0 : competitorIndex++) };
      });
  }, [trend, themeVariant, domainFilterId, competitors]);

  const { hidden, toggle } = useLegendHidden();
  const visibleSeries = useMemo(
    () => series.filter((brand) => !hidden.has(brand.name)),
    [series, hidden],
  );

  const option = useMemo(() => {
    if (!trend || !visibleSeries.length) return null;
    const daysWithData = trend.dates.filter((_, index) =>
      visibleSeries.some((brand) => brand.avgRank[index] != null),
    ).length;
    if (daysWithData === 0) return null;

    const chrome = chartChrome();
    const sparse = daysWithData <= 14;

    return {
      color: visibleSeries.map((brand) => brand.color),
      tooltip: {
        trigger: 'axis',
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number; axisValue: string }>) => {
          if (!params.length) return '';
          const index = params[0].dataIndex;
          const rows = visibleSeries
            .filter((brand) => brand.avgRank[index] != null)
            .map(
              (brand) => `<div style="margin-top:4px">
                <span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${brand.color};margin-inline-end:6px"></span>
                <strong>${escapeHtml(brand.name)}</strong> رتبه ${brand.avgRank[index]}
                <span style="color:${chrome.muted}"> · سهم ${brand.shareOfVoice?.[index] ?? 0}% · منشن ${brand.aiMentions[index]}</span></div>`,
            )
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${params[0].axisValue}</div>${rows || `<div style="color:${chrome.muted}">بدون داده</div>`}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 16, right: 40, top: 12, bottom: 28 },
      xAxis: {
        type: 'category',
        data: trend.dates,
        boundaryGap: sparse,
        axisLine: { lineStyle: { color: chrome.axisLine } },
        axisLabel: { color: chrome.axisLabel, fontSize: 11, fontFamily: PANEL_FONT_FAMILY },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        inverse: true,
        min: 1,
        max: (extent: { max: number }) => Math.max(5, Math.ceil(extent.max)),
        splitNumber: 4,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '#{value}',
        },
      },
      series: visibleSeries.map((brand) => ({
        name: brand.name,
        type: 'line',
        smooth: false,
        showSymbol: sparse,
        symbolSize: 8,
        lineStyle: { width: brand.isOwn ? 3 : 2 },
        z: brand.isOwn ? 3 : 2,
        data: brand.avgRank,
        connectNulls: true,
      })),
    };
  }, [trend, visibleSeries, isDark]);

  return (
    <Card className="h-100 shadow-sm">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-3"
        title={
          <div>
            روند سهم از دیده‌شدن
            <p className="fs--2 text-700 mb-0 fw-normal mt-2">
              میانگین رتبه روزانه شما و رقبا
            </p>
          </div>
        }
      />
      <Card.Body className="pt-2">
        {shouldShowDataLoadError(isError, error) ? (
          <DataLoadError
            compact
            error={error}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        ) : isFetching && !trend ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: 240 }}>
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            {option ? (
              <ReactECharts
                key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
                option={option}
                notMerge
                style={{ height: 240 }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <DashboardEmptyNote>
                {series.length > 0 && visibleSeries.length === 0
                  ? 'برندی برای نمایش انتخاب نشده.'
                  : 'در این بازه داده روزانه برای میانگین رتبه نیست.'}
              </DashboardEmptyNote>
            )}
            {series.length > 0 && (option != null || visibleSeries.length === 0) && (
              <ChartLegend
                onToggle={toggle}
                items={series.map((brand) => ({
                  id: brand.name,
                  label: brand.name,
                  color: brand.color,
                  bold: brand.isOwn,
                  hidden: hidden.has(brand.name),
                }))}
              />
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
